import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../Button.jsx';
import SearchInput from '../SearchInput.jsx';
import ViewToggle from '../ViewToggle.jsx';
import FilterSlot from './FilterSlot.jsx';
import FilterSortControls from './FilterSortControls.jsx';
import {
  FILTERS_ALL_FILTERS_BUTTON,
  FILTERS_CLEAR_ALL_BUTTON,
  FILTERS_ROOT_ROW,
  FILTERS_SEARCH_CLASS,
  FILTERS_SLOTS_ROW,
  FILTERS_RIGHT_DESKTOP_ONLY,
  FILTERS_RIGHT_GROUP,
  FILTERS_VIEW_TOGGLE_BUTTON,
} from './filtersTokens.js';
import {
  isSortDirty,
  partitionBarFilters,
  resolveSortDefaults,
} from './menuFilter.utils.js';
import { useFiltersBarSlotLimit } from './useFiltersBarSlotLimit.js';
import { useFiltersIsDesktopToolbar } from './useFiltersIsDesktopToolbar.js';
import { SlidersHorizontal } from 'lucide-react';

const defaultI18n = {
  removeFilter: (label) => `Remove ${label} filter`,
  addedAnnounce: (label) => `Filter added: ${label}`,
  removedAnnounce: (label) => `Filter removed: ${label}`,
  clearedAllAnnounce: 'All filters cleared',
  clearFilters: 'Clear filters',
  allFilters: 'All filters',
  moreChip: (count) => `+${count}`,
  sortAsc: 'Sort ascending',
  sortDesc: 'Sort descending',
};

/**
 * Figma-aligned filters toolbar: SearchInput + chip-based filter slots + sort/view/actions.
 * Desktop (`≥ xl`): barra completa con `justify-between`. Compact (`< xl`): solo search + «All filters», `justify-start`.
 *
 * @param {import('./filtersTypes.js').FiltersProps} props
 */
const Filters = ({
  searchProps,
  filters = [],
  maxFilters,
  maxVisibleFilters,
  onClearAll,
  actions = null,
  sort = null,
  viewToggle = null,
  showAllFilters = true,
  onAllFiltersClick,
  i18n: i18nProp,
  className = '',
  rowClassName = '',
}) => {
  const i18n = useMemo(() => ({ ...defaultI18n, ...i18nProp }), [i18nProp]);
  const [openSlotId, setOpenSlotId] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const responsiveSlotLimit = useFiltersBarSlotLimit();
  const isDesktopToolbar = useFiltersIsDesktopToolbar();
  const configuredMaxFilters = maxFilters ?? maxVisibleFilters;

  const { barFilters } = useMemo(
    () => partitionBarFilters(filters, configuredMaxFilters, responsiveSlotLimit),
    [filters, configuredMaxFilters, responsiveSlotLimit],
  );

  const searchOnClear = searchProps?.onClear;
  const searchOnChange = searchProps?.onChange;
  const hasSearch = searchProps != null;
  const hasBarFilters = barFilters.length > 0;
  const showAllFiltersButton = showAllFilters;

  const hasDesktopRight =
    actions != null ||
    sort != null ||
    (viewToggle != null && !viewToggle.hidden);

  const hasRight = isDesktopToolbar
    ? showAllFiltersButton || hasDesktopRight
    : showAllFiltersButton;

  useEffect(() => {
    if (!openSlotId) return;
    if (openSlotId === 'sort-field') {
      if (!isDesktopToolbar) setOpenSlotId(null);
      return;
    }
    if (!barFilters.some((f) => f.id === openSlotId)) {
      setOpenSlotId(null);
    }
  }, [barFilters, isDesktopToolbar, openSlotId]);

  const hasActiveSearch = useMemo(() => {
    const value = searchProps?.value;
    return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
  }, [searchProps?.value]);

  const hasActiveFilterSlots = useMemo(
    () => filters.some((f) => Array.isArray(f.value) && f.value.length > 0),
    [filters],
  );

  const sortIsDirty = useMemo(() => isSortDirty(sort), [sort]);

  const showClearAll =
    hasActiveSearch || (isDesktopToolbar && (hasActiveFilterSlots || sortIsDirty));

  const announce = useCallback((message) => {
    if (!message) return;
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(message));
  }, []);

  const handleOpenSlot = useCallback((slotId) => {
    setOpenSlotId(slotId);
  }, []);

  const handleOpenSort = useCallback(() => {
    setOpenSlotId('sort-field');
  }, []);

  const handleCloseSlot = useCallback(() => {
    setOpenSlotId(null);
  }, []);

  const resetSort = useCallback(() => {
    if (!sort) return;

    const { value: defaultValue, direction: defaultDirection } = resolveSortDefaults(sort);

    if (defaultValue != null && sort.value !== defaultValue) {
      sort.onChange?.(defaultValue);
    }

    if ((sort.direction ?? 'desc') !== defaultDirection) {
      sort.onDirectionChange?.(defaultDirection);
    }
  }, [sort]);

  const handleClearAll = useCallback(() => {
    if (!showClearAll) return;

    filters.forEach((filter) => {
      if (Array.isArray(filter.value) && filter.value.length > 0) {
        filter.onChange?.([]);
      }
    });

    if (searchOnClear) {
      searchOnClear();
    } else if (searchOnChange) {
      searchOnChange('');
    }

    resetSort();
    onClearAll?.();
    announce(i18n.clearedAllAnnounce);
    setOpenSlotId(null);
  }, [filters, searchOnClear, searchOnChange, showClearAll, resetSort, onClearAll, announce, i18n.clearedAllAnnounce]);

  const resolvedSearchProps = useMemo(() => {
    if (!searchProps) return null;
    return {
      placeholder: 'Search...',
      ...searchProps,
      className: [FILTERS_SEARCH_CLASS, searchProps.className].filter(Boolean).join(' '),
    };
  }, [searchProps]);

  const rowClass = [FILTERS_ROOT_ROW, rowClassName].filter(Boolean).join(' ');

  return (
    <div className={['shrink-0', className].filter(Boolean).join(' ')}>
      <div className={rowClass}>
        <div className={FILTERS_SLOTS_ROW}>
          {hasSearch ? <SearchInput {...resolvedSearchProps} /> : null}

          {hasBarFilters
            ? barFilters.map((filter) => (
                <FilterSlot
                  key={filter.id}
                  config={filter}
                  isOpen={openSlotId === filter.id}
                  onOpen={handleOpenSlot}
                  onClose={handleCloseSlot}
                  onAnnounce={announce}
                  removeAriaLabel={i18n.removeFilter}
                  removedAnnounce={i18n.removedAnnounce}
                  addedAnnounce={i18n.addedAnnounce}
                  moreChip={i18n.moreChip}
                />
              ))
            : null}

          {showClearAll ? (
            <button
              type="button"
              onClick={handleClearAll}
              className={FILTERS_CLEAR_ALL_BUTTON}
            >
              {i18n.clearFilters}
            </button>
          ) : null}
        </div>

        {hasRight ? (
          <div className={FILTERS_RIGHT_GROUP}>
            {showAllFiltersButton ? (
              <Button
                type="button"
                variant="secondary"
                size="md"
                pill
                rightIcon={SlidersHorizontal}
                customButtonHeight="!h-10"
                customFontSize="text-sm leading-5"
                customIconSize={18}
                className={FILTERS_ALL_FILTERS_BUTTON}
                onClick={onAllFiltersClick}
              >
                {i18n.allFilters}
              </Button>
            ) : null}

            <div className={FILTERS_RIGHT_DESKTOP_ONLY}>
              {sort ? (
                <FilterSortControls
                  sort={sort}
                  isOpen={openSlotId === 'sort-field'}
                  onOpen={handleOpenSort}
                  onClose={handleCloseSlot}
                  i18n={{ sortAsc: i18n.sortAsc, sortDesc: i18n.sortDesc }}
                />
              ) : null}

              {viewToggle && !viewToggle.hidden ? (
                <ViewToggle
                  view={viewToggle.value}
                  onChange={viewToggle.onChange}
                  options={viewToggle.options}
                  ariaLabel={viewToggle.ariaLabel}
                  buttonClassName={FILTERS_VIEW_TOGGLE_BUTTON}
                />
              ) : null}

              {actions}
            </div>
          </div>
        ) : null}
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
};

export default memo(Filters);
